import type { fieldLabels } from '@/features/consignment-form/labels'
import type { Product } from '@/features/consignment-form/products'
import type { Consignment, Item } from '@/features/consignment-form/schema'
import { consignmentSchema, editedItemSchema } from '@/features/consignment-form/schema'
import { normalisePhone } from '@/features/consignment-form/rules'
import { isoFromUk } from '@/lib/date'

/** Estimate body using the field names confirmed by f-edge. */
export function toEstimatePayload(item: Item, products: readonly Product[]) {
  const valid = editedItemSchema(products).parse(item)

  return {
    quantity: Number(valid.qty),
    tariffCode: valid.category,
    description: valid.description,
    ...(valid.weightKg === '' ? {} : { weightInKg: Number(valid.weightKg) }),
    // The schema keeps the three sides together, so one of them present means all three are.
    ...(valid.lengthCm === ''
      ? {}
      : {
          lengthInCm: Number(valid.lengthCm),
          widthInCm: Number(valid.widthCm),
          heightInCm: Number(valid.heightCm),
        }),
  }
}

const toPartyDto = (value: Consignment['shipper']) => {
  const phone = normalisePhone(value.contactNumber)
  if (!phone) throw new Error('Invalid phone')
  return {
    firstName: value.name,
    lastName: value.surname,
    phone,
    email: value.email,
    ...(value.legalEntity
      ? { companyName: value.companyName, address: value.street, zip: value.postcode, city: value.locality }
      : {}),
  }
}

const partyFields = {
  firstName: 'name',
  lastName: 'surname',
  phone: 'contactNumber',
  email: 'email',
  companyName: 'companyName',
  address: 'street',
  zip: 'postcode',
  city: 'locality',
} as const

const topFields = {
  date: 'readyFrom',
  departurePort: 'departurePort',
  destinationPort: 'departurePort',
  paymentFrom: 'paidBy',
  paymentMethod: 'paymentMethod',
} as const

const itemFields = {
  quantity: 'qty',
  tariffCode: 'category',
  description: 'description',
  weightInKg: 'weightKg',
  lengthInCm: 'lengthCm',
  widthInCm: 'widthCm',
  heightInCm: 'heightCm',
} as const

const keyOf = <T extends object>(record: T, key: string | undefined): key is Extract<keyof T, string> =>
  key !== undefined && Object.hasOwn(record, key)

/** The item editor field an estimate DTO field comes from, as f-edge names it in an error. */
export const itemFieldOf = (dtoField: string) => (keyOf(itemFields, dtoField) ? itemFields[dtoField] : undefined)

/**
 * Where a create DTO path named in a rejection lives in the form, the reverse of the payload below:
 * a labelled form field, or an item row and its field. Nothing for a path the form does not show.
 */
export function formFieldOf(
  dtoPath: string
): { path: keyof typeof fieldLabels } | { item: number; field: keyof Item } | undefined {
  const [, owner, index, field] = /^(\w+)(?:\[(\d+)\])?(?:\.(\w+))?$/.exec(dtoPath) ?? []
  if ((owner === 'shipper' || owner === 'consignee') && keyOf(partyFields, field)) {
    return { path: `${owner}.${partyFields[field]}` }
  }
  if (owner === 'items' && index !== undefined && keyOf(itemFields, field)) {
    return { item: Number(index), field: itemFields[field] }
  }
  if (field === undefined && index === undefined && keyOf(topFields, owner)) return { path: topFields[owner] }
  return undefined
}

/** Build the confirmed create fields. */
export function toCreatePayload(values: Consignment, products: readonly Product[]) {
  const valid = consignmentSchema.parse(values)

  return {
    shipper: toPartyDto(valid.shipper),
    consignee: toPartyDto(valid.consignee),
    // The schema above has turned away anything that is not a real dd/mm/yyyy day; the route checks again.
    date: isoFromUk(valid.readyFrom) ?? '',
    departurePort: valid.departurePort,
    destinationPort: valid.destinationPort,
    paymentFrom: valid.paidBy,
    paymentMethod: valid.paymentMethod,
    items: valid.items.map((item) => toEstimatePayload(item, products)),
  }
}
